// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export interface ProjectPlannerStyles {
    widgetWrapper?: InjectableCss;
    container?: InjectableCss;
    titleSection?: InjectableCss;
    stepWrapper?: InjectableCss;
    stepTitle?: TypographyPresentationProps;
    descriptionText?: TypographyPresentationProps;
    groupsWrapper?: InjectableCss;
    groupItem?: ClickablePresentationProps;
    addNewGroupItem?: InjectableCss;
    addNewButton?: ButtonPresentationProps;
    groupNameText?: TypographyPresentationProps;
    urlStyling?: LinkPresentationProps;
    title?: TypographyPresentationProps;
    primaryButton?: ButtonPresentationProps;
    secondaryButton?: ButtonPresentationProps;
    newListInput?: TextFieldPresentationProps;
    notifyStyle?: InjectableCss;
}

export const styles: ProjectPlannerStyles = {
    widgetWrapper: {
        css: css`
            ${(props: any) => props.backgroundImage && `background-image: url(${props.backgroundImage});`}
            background-size: cover;
            background-repeat: no-repeat;
            position: relative;
            height: 700px;
            width: 100%;
            background-color: ${getColor("common.ui2")};
        `,
    },
    titleSection: {
        css: css`
            max-width: 900px;
            background: white;
            box-shadow: 0 2px 11px 0 rgba(53, 65, 94, 0.25);
            position: absolute;
            left: 50%;
            top: -80px;
            padding: 30px 40px;
            transform: translateX(-50%);
            width: 100%;
            @media (max-width: 480px) {
                top: 0;
            }
        `,
    },
    title: {
        variant: "h2",
        css: css`
            margin-bottom: 0;
        `,
    },
    urlStyling: {
        css: css`
            font-size: 36px;
            line-height: 38px;
        `,
        typographyProps: {
            css: css`
                &&& svg {
                    stroke-width: 3px;
                }
            `,
        },
    },
    container: {
        css: css`
            position: absolute;
            background: white;
            max-width: 678px;
            width: 100%;
            padding: 40px;
            left: 50%;
            bottom: -40px;
            z-index: 4;
            box-shadow: 0 2px 11px 0 rgba(53, 65, 9, 0.25);
            transform: translateX(-50%);
            .modal-actions {
                margin-top: 30px;
                @media (max-width: 479px) {
                    text-align: center;
                }
            }
            @media (max-width: 760px) {
                padding: 40px 20px;
            }

            @media (max-width: 600px) {
                left: 0;
                right: 0;
                transform: none;
            }
        `,
    },
    stepWrapper: {
        css: css`
            height: 0;
            overflow: hidden;
            opacity: 0;
            position: absolute;
            right: -100%;
            transition: all 400ms;

            &&.in-view {
                height: auto;
                overflow-y: auto;
                opacity: 1;
                position: static;
                transition: all 200ms;
                @media (max-width: 600px) {
                    max-height: 350px;
                }
            }
        `,
    },
    stepTitle: {
        variant: "h4",
        css: css`
            margin-bottom: 0;
        `,
    },
    descriptionText: {
        size: fontConfig.fontSize.regular,
        lineHeight: fontConfig.lineHeight.small,
    },
    groupsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            margin-right: -40px;
            @media (max-width: 760px) {
                margin-right: -20px;
            }
            @media (max-width: 600px) {
                margin-right: 0;
            }
        `,
    },
    groupItem: {
        css: css`
            width: calc(100% / 4 - 40px);
            margin-right: 40px;
            text-align: center;
            display: block;
            margin-top: 30px;

            .item-wrapper {
                border: 1px solid transparent;
                display: flex;
                flex-direction: column;
                align-items: center;
            }

            &&.selected .item-wrapper {
                border-color: ${getColor("primary")};
            }

            @media (max-width: 760px) {
                width: calc(100% / 4 - 20px);
                margin-right: 20px;
                margin-top: 20px;
            }
            @media (max-width: 600px) {
                width: calc(100% / 2);
                margin-right: 0;
                margin-top: 20px;
            }
            @media (max-width: 400px) {
                width: calc(100%);
                margin-right: 0;
                margin-top: 20px;
            }
        `,
    },
    addNewGroupItem: {
        css: css`
            width: 100%;
            height: 120px;
            border: 2px dashed ${getColor("common.ui3")};
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 15px;
        `,
    },
    addNewButton: {
        css: css`
            padding: 0;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            svg polygon {
                fill: ${getColor("text.main")};
            }
            &&:hover {
                svg polygon {
                    fill: ${getColor("common.white")};
                }
            }
        `,
    },
    groupNameText: {
        size: fontConfig.fontSize.small,
        weight: fontConfig.weight.semiBold,
        lineHeight: "17px",
        color: "text.main",
        transform: "uppercase",
        css: css`
            letter-spacing: 1px;
        `,
    },
    primaryButton: {
        typographyProps: {
            css: css`
                &&&& {
                    font-size: ${fontConfig.fontSize.small}px;
                }
            `,
        },
        css: css`
            &&&& {
                min-height: 36px;
                margin-right: 20px;
                @media (max-width: 430px) {
                    margin: 0;
                }
            }
        `,
    },
    secondaryButton: {
        typographyProps: {
            css: css`
                &&&& {
                    font-size: ${fontConfig.fontSize.small}px;
                }
            `,
        },
        css: css`
            &&&& {
                min-height: 36px;
                background: transparent;
                color: ${getColor("common.ui1")};
                padding: 0 20px;
                &&:focus {
                    border: 0;
                    outline: 0 !important;
                    text-decoration: underline;
                }
            }
        `,
    },
    newListInput: {
        cssOverrides: {
            formField: css`
                max-width: 410px;
                margin-top: 110px;
                margin-bottom: 195px;
                @media (max-width: 760px) {
                    margin-top: 50px;
                    margin-bottom: 80px;
                }
                @media (max-width: 480px) {
                    margin-top: 20px;
                    margin-bottom: 30px;
                }
            `,
        },
    },
    notifyStyle: {
        css: css`
            margin-top: 15px;
            width: fit-content;
            max-width: 100%;
            min-width: 540px;
        `,
    },
};
